import {
  createProjectEvent as createEventRequest,
  deleteProjectEvent as deleteEventRequest,
  getProjectEvents,
  updateProjectEvent as updateEventRequest,
} from "helpers/apiClient";

const eventTypes = new Set(["event", "notification", "queue"]);
const eventFilters = new Set(["all", ...eventTypes]);

const eventId = (eventOrId) => {
  const value = typeof eventOrId === "object" && eventOrId !== null
    ? eventOrId.event_id ?? eventOrId.id
    : eventOrId;
  return value === undefined || value === null || value === "" ? null : String(value);
};

const dateTime = (value) => {
  if (value instanceof Date) return value.toISOString();
  return new Date(value).toISOString();
};

const assignedValue = (assigned) =>
  Array.isArray(assigned) ? assigned.join(", ") : assigned;

const optionalNumber = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

export const toLegacyEvent = (event) => ({
  ...event,
  event_id: event.event_id ?? event.id,
  user_id: event.user_id ?? event.createdByUserId,
  duration: event.duration ?? event.durationMinutes ?? null,
  parcela: event.parcela ?? event.parcelReference ?? null,
  parcel_order: event.parcel_order ?? event.parcelOrder ?? null,
});

export const toEventCreateRequest = (event) => {
  const type = event.type;
  const payload = {
    title: event.title,
    start: dateTime(event.start),
    type,
  };

  if (type === "queue") {
    let durationMinutes = optionalNumber(event.durationMinutes ?? event.duration);
    if (durationMinutes === undefined && event.end !== undefined) {
      durationMinutes = Math.round(
        (new Date(event.end).getTime() - new Date(event.start).getTime()) / 60000
      );
    }
    payload.durationMinutes = durationMinutes;
    payload.queryType = "queue";
  } else {
    payload.end = dateTime(event.end);
  }

  const urgency = optionalNumber(event.urgency);
  const parcelReference = optionalNumber(event.parcelReference ?? event.parcela);
  const assigned = assignedValue(event.assigned);
  if (urgency !== undefined) payload.urgency = urgency;
  if (assigned !== undefined) payload.assigned = assigned;
  if (event.comments !== undefined) payload.comments = event.comments;
  if (parcelReference !== undefined) payload.parcelReference = parcelReference;
  if (type !== "queue" && event.queryType !== undefined) {
    payload.queryType = event.queryType;
  }
  return payload;
};

export const toEventUpdateRequest = (event) => {
  if (event.type === "queue") {
    const payload = {
      durationMinutes: optionalNumber(event.durationMinutes ?? event.duration),
    };
    if (event.start !== undefined) payload.start = dateTime(event.start);
    return payload;
  }
  const payload = {};
  if (event.title !== undefined) payload.title = event.title;
  if (event.start !== undefined) payload.start = dateTime(event.start);
  if (event.end !== undefined) payload.end = dateTime(event.end);
  if (event.durationMinutes !== undefined || event.duration !== undefined) {
    payload.durationMinutes = optionalNumber(event.durationMinutes ?? event.duration);
  }
  if (event.urgency !== undefined) payload.urgency = optionalNumber(event.urgency);
  if (event.assigned !== undefined) payload.assigned = assignedValue(event.assigned);
  if (event.comments !== undefined) payload.comments = event.comments;
  if (event.type !== undefined) payload.type = event.type;
  if (event.parcelReference !== undefined || event.parcela !== undefined) {
    payload.parcelReference = optionalNumber(event.parcelReference ?? event.parcela);
  }
  return payload;
};

export const loadProjectEvents = async (projectId, type = "all") => {
  if (!projectId) return [];
  if (!eventFilters.has(type)) throw new Error("Invalid Event filter.");
  const response = await getProjectEvents(projectId, type);
  const events = Array.isArray(response) ? response : response?.events || [];
  return events.map(toLegacyEvent);
};

export const createEvent = async (projectId, event) => {
  if (!projectId) return null;
  if (!eventTypes.has(event?.type)) throw new Error("Invalid Event type.");
  const response = await createEventRequest(projectId, toEventCreateRequest(event));
  return response ? toLegacyEvent(response) : response;
};

export const updateEvent = async (projectId, eventOrId, event) => {
  const id = eventId(eventOrId);
  if (!projectId || !id) return null;
  const response = await updateEventRequest(projectId, id, toEventUpdateRequest(event));
  return response ? toLegacyEvent(response) : response;
};

export const deleteEvent = async (projectId, eventOrId) => {
  const id = eventId(eventOrId);
  if (!projectId || !id) return null;
  return deleteEventRequest(projectId, id);
};
